/* eslint-disable prefer-const */
/** 전역 라이브러리 */
import React, {
  FunctionComponent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { css } from '@emotion/react';
import { ErrorMessage } from '@hookform/error-message';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EventIcon from '@mui/icons-material/Event';
import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import { createPortal } from 'react-dom';
import { FieldErrors, useController } from 'react-hook-form';
import Select from 'react-select';

/** 공통 컴포넌트 */
import Button from '@components/Common/Button';
import Layer from '@components/Common/Layer';

/** 전역 유틸리티 */
import { palette } from '@styles/theme';

dayjs.extend(customParseFormat);

/** 요일 Options */
const WEEKDAY: string[] = ['일', '월', '화', '수', '목', '금', '토'];

/** 입력 문자열을 YYYY-MM-DD 형식으로 정규화 (숫자만 추출 후 하이픈 삽입) */
const normalizeDateInput = (raw: string): string => {
  const digits = raw.replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 4) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 4)}-${digits.slice(4)}`;
  return `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6)}`;
};

/** 연도 상/하 Depth 설정 */
const YEAR_DEPTH = 20;

interface Props {
  name: string;
  fieldName?: string;
  rules: any;
  errors: FieldErrors;
  control: any;
  layerPosition?: 'bottom' | 'top';
  isErrorText?: boolean;
  errorTextAlign?: 'left' | 'center' | 'right';
}

const DatePicker: FunctionComponent<Props> = ({
  name,
  fieldName,
  rules,
  errors,
  control,
  layerPosition = 'bottom',
  isErrorText = true,
  errorTextAlign = 'right',
}) => {
  const [datePickerOpen, setDatePickerOpen] = useState<boolean>(false);
  const [initDt, setInitDt] = useState<Dayjs | undefined>(dayjs());
  const inputRef = useRef<HTMLDivElement>(null);

  const { field } = useController({
    control: control,
    name: name,
    rules: rules,
  });
  const [value, setValue] = useState(String(field.value));
  const [defaultYearValue, setDefaultYearValue] = useState({
    label: `${dayjs().format('YYYY')}년`,
    value: dayjs().format('YYYY'),
  });
  const [defaultMonthValue, setDefaultMonthValue] = useState({
    label: `${dayjs().format('M')}월`,
    value: dayjs().format('M'),
  });

  /** 월 Options */
  const monthOptions: any[] = [...Array(12)].map((_, i: number) => ({
    value: String(i + 1),
    label: `${i + 1}월`,
  }));

  /** 연도 Options */
  const yearOptions: any[] = useMemo(
    () => [
      ...[...Array(YEAR_DEPTH)].map((_, i: number) => ({
        value: dayjs()
          .subtract(YEAR_DEPTH - i, 'years')
          .format('YYYY'),
        label: `${dayjs()
          .subtract(YEAR_DEPTH - i, 'years')
          .format('YYYY')}년`,
      })),
      ...[
        {
          value: dayjs().format('YYYY'),
          label: dayjs().format('YYYY년'),
        },
      ],
      ...[...Array(YEAR_DEPTH)].map((_, i: number) => ({
        value: dayjs()
          .add(1 + i, 'years')
          .format('YYYY'),
        label: `${dayjs()
          .add(1 + i, 'years')
          .format('YYYY')}년`,
      })),
    ],
    []
  );

  useEffect(() => {
    setValue(String(field?.value || ''));
  }, [field.value, setValue]);

  useEffect(() => {
    setDefaultYearValue({
      label: `${dayjs(initDt).format('YYYY')}년`,
      value: dayjs(initDt).format('YYYY'),
    });
    setDefaultMonthValue({
      label: `${dayjs(initDt).format('M')}월`,
      value: dayjs(initDt).format('M'),
    });
  }, [initDt]);

  /** SelectBox 월 변경 */
  const handleMonthSelect = (e: { value: string; label: string }) => {
    setInitDt(dayjs(initDt).month(parseInt(e.value) - 1));
  };

  /** SelectBox 연도 변경 */
  const handleYearSelect = (e: { value: string; label: string }) => {
    setInitDt(dayjs(initDt).year(parseInt(e.value)));
    setDefaultYearValue({
      label: dayjs(initDt).year(parseInt(e.value)).format('YYYY년'),
      value: dayjs(initDt).year(parseInt(e.value)).format('YYYY'),
    });
  };

  /** 이전달 */
  const handlePrevMonth = () => {
    setInitDt(dayjs(initDt).subtract(1, 'month'));
  };

  /** 다음달 */
  const handleNextMonth = () => {
    setInitDt(dayjs(initDt).add(1, 'month'));
  };

  /** 초기화 */
  const handleInit = () => {
    setInitDt(undefined);
    setValue('');
    field.onChange('');
    setDatePickerOpen(false);
  };

  /** 날짜 선택 */
  const handleDtChange = (date: Dayjs) => {
    setValue(dayjs(date).format('YYYY-MM-DD'));
    field.onChange(dayjs(date).format('YYYY-MM-DD'));
    setDatePickerOpen(false);
  };

  /** 텍스트 직접 입력 (타이핑) */
  const handleTextInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = normalizeDateInput(e.target.value);
    setValue(formatted);

    if (formatted === '') {
      field.onChange('');
      return;
    }

    const parsed = dayjs(formatted, 'YYYY-MM-DD', true);
    if (parsed.isValid()) {
      field.onChange(formatted);
      setInitDt(parsed);
    }
  };

  /** 붙여넣기 */
  const handleTextPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text');
    const formatted = normalizeDateInput(pasted);
    setValue(formatted);

    const parsed = dayjs(formatted, 'YYYY-MM-DD', true);
    if (parsed.isValid()) {
      field.onChange(formatted);
      setInitDt(parsed);
    }
  };

  /** Blur 시 미완성/유효하지 않은 값은 form value로 롤백 */
  const handleTextBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (value === '') {
      field.onChange('');
    } else {
      const parsed = dayjs(value, 'YYYY-MM-DD', true);
      if (!parsed.isValid()) {
        setValue(String(field.value || ''));
      }
    }
    field.onBlur();
    e.stopPropagation();
  };

  /** 달력 그리기 함수 */
  const generate = () => {
    dayjs.extend(weekOfYear);

    const today = initDt || dayjs();
    const startWeek = today.startOf('month').week();
    const endWeek =
      today.endOf('month').week() === 1 ? 53 : today.endOf('month').week();

    let calendar: any = [];
    for (let week = startWeek; week <= endWeek; week++) {
      calendar.push(
        <div css={weeks} key={week}>
          {Array(7)
            .fill(0)
            .map((n, i) => {
              let current = today
                .week(week)
                .startOf('week')
                .add(n + i, 'day');
              let isToday =
                current.format('YYYY-MM-DD') === dayjs().format('YYYY-MM-DD');
              let isSelected =
                value !== '' && current.format('YYYY-MM-DD') === value;
              let isGrayed =
                current.format('MM') === today.format('MM') ? '' : 'grayed';

              return (
                <div
                  key={`${i}_${n}`}
                  className={`days ${
                    isGrayed
                      ? 'daysOff'
                      : isSelected
                      ? 'daysSelect'
                      : isToday
                      ? 'daysToday'
                      : ''
                  }`}
                  onClick={() => handleDtChange(current)}
                >
                  {current.format('MM') === today.format('MM')
                    ? current.format('D')
                    : ''}
                </div>
              );
            })}
        </div>
      );
    }

    return calendar;
  };

  // 캘린더 위치 계산
  const [calendarPosition, setCalendarPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);

  // 위치 계산 함수 (requestAnimationFrame으로 최적화)
  const updateCalendarPosition = () => {
    if (inputRef.current) {
      requestAnimationFrame(() => {
        if (inputRef.current) {
          const rect = inputRef.current.getBoundingClientRect();
          setCalendarPosition({
            top: layerPosition === 'bottom' ? rect.bottom + 25 : rect.top - 340,
            left: rect.left,
          });
        }
      });
    }
  };

  useEffect(() => {
    if (datePickerOpen && inputRef.current) {
      updateCalendarPosition();

      // 스크롤 및 리사이즈 시 위치 업데이트
      // requestAnimationFrame으로 최적화되어 있으므로 throttle 불필요
      window.addEventListener('scroll', updateCalendarPosition, true);
      window.addEventListener('resize', updateCalendarPosition);

      return () => {
        window.removeEventListener('scroll', updateCalendarPosition, true);
        window.removeEventListener('resize', updateCalendarPosition);
      };
    } else {
      setCalendarPosition(null);
    }
  }, [datePickerOpen, layerPosition]);

  return (
    <div css={{ position: 'relative' }}>
      {/* Input Component */}
      <div ref={inputRef} css={inputWrap}>
        <div
          css={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            padding: '6px 0',
          }}
        >
          <input
            css={inputStyle}
            type={'text'}
            value={value}
            autoComplete="off"
            placeholder={'YYYY-MM-DD'}
            maxLength={10}
            onChange={handleTextInput}
            onPaste={handleTextPaste}
            onBlur={handleTextBlur}
            onFocus={() => setDatePickerOpen(true)}
            name={name}
            ref={field.ref}
          />
          {isErrorText && errors[fieldName ? fieldName : name] && (
            <ErrorMessage
              errors={errors}
              name={name}
              render={({ message }) => (
                <p css={errorMessage(errorTextAlign)}>{message}</p>
              )}
            />
          )}
        </div>
        <div
          className="icon"
          onClick={() => setDatePickerOpen(!datePickerOpen)}
        >
          <EventIcon />
        </div>
      </div>

      {/* DatePicker Component - Portal로 body에 렌더링 */}
      {datePickerOpen &&
        calendarPosition &&
        createPortal(
          <div
            css={css`
              position: fixed;
              top: ${calendarPosition.top}px;
              left: ${calendarPosition.left}px;
              z-index: 1000;
            `}
          >
            <Layer
              open={datePickerOpen}
              handleOpen={() => {
                setDatePickerOpen(false);
              }}
            >
              <div css={container(layerPosition)}>
                {/* Head */}
                <div css={headContainer}>
                  <div css={headDate}>
                    <div>
                      <Select
                        options={yearOptions}
                        value={defaultYearValue}
                        onChange={(e: any) => handleYearSelect(e)}
                        placeholder={'연도'}
                        components={{
                          IndicatorSeparator: () => null,
                        }}
                        css={{ fontSize: '14px', width: '110px' }}
                        theme={(theme) => ({
                          ...theme,
                          colors: {
                            ...theme.colors,
                            primary: palette.secondaryColor,
                          },
                          fontSize: 12,
                        })}
                      />
                    </div>
                    <div>
                      <Select
                        options={monthOptions}
                        value={defaultMonthValue}
                        onChange={(e: any) => handleMonthSelect(e)}
                        placeholder={'월'}
                        components={{
                          IndicatorSeparator: () => null,
                        }}
                        css={{ fontSize: '14px', width: '85px' }}
                        theme={(theme) => ({
                          ...theme,
                          colors: {
                            ...theme.colors,
                            primary: palette.secondaryColor,
                          },
                          fontSize: 12,
                        })}
                      />
                    </div>
                  </div>
                  <div css={headArrowWrap}>
                    <div className="buttons" onClick={handlePrevMonth}>
                      <ChevronLeftIcon />
                    </div>
                    <div className="buttons" onClick={handleNextMonth}>
                      <ChevronRightIcon />
                    </div>
                  </div>
                </div>
                {/* Body */}
                <div css={bodyContainer}>
                  {/* Week Wrap */}
                  <div css={weekWrap}>
                    {WEEKDAY.map((d) => (
                      <div css={week} key={d}>
                        <div>{d}</div>
                      </div>
                    ))}
                  </div>
                  {generate()}
                </div>

                <div css={btnWrap}>
                  <Button
                    label="초기화"
                    theme="grayscale"
                    fontSize={12}
                    height={30}
                    handleClick={handleInit}
                  />
                </div>
              </div>
            </Layer>
          </div>,
          document.body
        )}
    </div>
  );
};

export default DatePicker;

const container = (layerPosition: 'bottom' | 'top') => css`
  width: 320px;
  display: flex;
  flex-direction: column;
  border-radius: 10px;
  box-shadow: 0px 5px 16px rgba(0, 0, 0, 0.08);

  position: absolute;
  background: ${palette.white};
  ${layerPosition === 'bottom' &&
  css`
    top: -28px;
  `}
  ${layerPosition === 'top' &&
  css`
    bottom: 68px;
  `}
`;

const headContainer = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 55px;
  padding: 15px 15px 0 15px;
`;

const headDate = css`
  display: flex;
  gap: 5px;
  font-size: 18px;
`;

const headArrowWrap = css`
  display: flex;
  gap: 5px;
  .buttons {
    display: flex;
    // width: 25px;
    width: 38px;
    height: 38px;
    align-items: center;
    justify-content: center;
    border-radius: 5px;
    cursor: pointer;
    transition: 0.3s;
    &:hover {
      background-color: ${palette.gray3};
    }
    &:active {
      background-color: ${palette.gray4};
    }
  }
`;

const bodyContainer = css`
  padding: 15px;
  height: 311px;
`;

const weekWrap = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const weeks = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  .days {
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    cursor: pointer;
    transition: 0.3s;
    &:hover {
      background-color: ${palette.gray3};
    }
    &:active {
      background-color: ${palette.gray4};
    }
  }
  .daysOff {
    cursor: inherit;
    &:hover,
    &active {
      background-color: ${palette.white};
    }
  }
  .daysToday {
    border: 1px solid ${palette.primaryColor};
    color: ${palette.primaryColor};
  }
  .daysSelect {
    background-color: ${palette.primaryColor};
    color: ${palette.white};
    &:hover,
    &active {
      background-color: ${palette.primaryColor};
    }
  }
  & + & {
    margin-top: 5px;
  }
`;

const week = css`
  width: 36px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const inputWrap = css`
  position: relative;
  transition: 0.3s;

  .icon {
    position: absolute;
    top: 8px;
    left: 95px;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    svg {
      fill: ${palette.gray5};
    }
  }
  &:hover {
    svg {
      fill: ${palette.gray6};
    }
  }
`;

const inputStyle = css`
  display: flex;
  // width: calc(100% - 40px);
  width: 110px;
  height: 36px;
  border-radius: 5px;
  border: 1px solid ${palette.gray3};
  padding: 0 10px;
  background-color: ${palette.white};
  position: relative;
  cursor: text;

  :focus {
    outline: none;
    border-color: ${palette.gray5};
  }
  &[readOnly] {
    // background-color: ${palette.gray1};
    color: ${palette.gray6};
  }
`;

const errorMessage = (textAlign: 'left' | 'center' | 'right') => css`
  color: ${palette.colorDanger};
  font-size: 12px;
  padding: 3px 5px;
  height: 22px;
  text-align: ${textAlign};
`;

const btnWrap = css`
  display: flex;
  padding: 0px 15px 10px 15px;
  gap: 5px;
  justify-content: flex-end;
`;
